<script setup>
import { ref, onMounted, computed} from 'vue'
import { Dialog } from 'quasar'
import { getI18n } from '@/plugins/i18n'

import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore, portalAccessStatusEnum, infoBoxIdEnum } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { useNotify } from '@/composables/notify'
import { useStepper } from '@/composables/stepper'
import { useIsUpdated } from '@/composables/object'
import { useNavigator } from '@/composables/navigator'
import { useEscapeClose } from '@/composables/keyboard'
import { useDetectDevice } from '@/composables/device'
import { useArchive } from '@/composables/archive'
import { useClone } from '@/composables/object'
import { useValidation } from '@/composables/validation'
import { buildTagPayload } from '@/composables/tag'
import { stringToColor, textColor } from '@/lib/utils'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import FormSectionTitle from '@/components/shared/FormSectionTitle.vue'
import StaffPersonalFields from '@/components/shared/StaffPersonalFields.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import CameraGalleryInput from '@/components/shared/CameraGalleryInput.vue'
import RateCardInfo from '@/components/shared/RateCardInfo.vue'
import InfoBox from '@/components/shared/InfoBox.vue'
import FileUrlEdit from '@/components/shared/FileUrlEdit.vue'
import WarehouseSelector from '@/components/shared/WarehouseSelector.vue'
import CreateModeToggle from '@/components/shared/CreateModeToggle.vue'
import PortalAccessStatusChip from '@/components/shared/PortalAccessStatusChip.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  changes: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  initialQuickCreate: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const { step, nextStep, prevStep, markAttempted, wasAttempted } = useStepper()
const { notifySuccess, notifyError } = useNotify()
const { emailValidator } = useValidation()

const planningStore = usePlanningStore()
const accountingStore = useAccountingStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()

const form = ref(null)
const quickCreate = ref(props.initialQuickCreate && !props.modelValue.id)
const quickStep = ref(1)
const cameraGalleryInput = ref(null)
const editItem = ref(props.modelValue.id ? useClone(props.modelValue) : {
  availability_type: portalStore.serviceAvailabilityTypeEnum.USUALLY_AVAILABLE,
  currency_ref: webSiteStore.getDefaultCurrency().id,
  type: portalStore.serviceTypeEnum.STAFF,
  warehouse: webSiteStore.getDefaultWarehouse(),
  tags: [],
  rc_price: null,
  rc_cost: null,
  rc_currency_ref: webSiteStore.getDefaultCurrency()?.id,
  rc_unit_of_measure_ref: webSiteStore.getDefaultUnitOfMeasure()?.id,
  rc_vat_type_ref: webSiteStore.getDefaultVatType()?.id,
  ...useClone(props.modelValue),
})

const { isUpdated, original, isUpdating, setOriginal } = useIsUpdated(editItem)

onMounted(async () => {
  if (props.modelValue.id) {
    editItem.value = { ...(await planningStore.fetchStaffByIdAsync(props.modelValue.id)).data }
    setOriginal(editItem.value)
    Object.assign(editItem.value, props.changes)
  } else {
    // Prefill the code with the next database id (editable hint).
    editItem.value.code = String(await planningStore.fetchNextStaffIdAsync())
    setOriginal(editItem.value)
  }
})

const localizedServiceAvailabilityTypes = portalStore.getLocalizedServiceAvailabilityTypes()
const showResendableStatus = computed(() =>
  !!editItem.value.id
  && [portalAccessStatusEnum.PENDING, portalAccessStatusEnum.EXPIRED].includes(editItem.value.portal_access_status)
  && webSiteStore.can(webSiteStore.permissionFlagEnum.USERS_PERMISSIONS)
)

const isResending = ref(false)
const onResendInvite = async () => {
  isResending.value = true
  try {
    await planningStore.resendInviteStaffAsync(editItem.value.id)
    editItem.value.portal_access_status = portalAccessStatusEnum.PENDING
    notifySuccess(i18n.t('info.userActivationEmailSent'))
  } finally {
    isResending.value = false
  }
}

const addServiceTag = async (value, doneFn) => {
  const existingTag = planningStore.getLocalizedServiceTags().find(t => t.name.toLowerCase() === value.toLowerCase())
  if (existingTag) {
    doneFn(existingTag.id)
    return
  }
  const created = (await planningStore.addServiceTagAsync(buildTagPayload(value, { name: value }))).data
  await planningStore.fetchServiceTagsAsync({ all: true }, true)
  doneFn(created.id)
}
const addPrimaryServiceTag = (value, doneFn) => addServiceTag(value, doneFn)

const { archiveWarning, archiveAsync, onRowRemoving: onDelete } = useArchive({
  onArchive: async(id) => { await planningStore.archiveStaffAsync(id) },
  onDelete: async(id, config) => { 
    await planningStore.deleteStaffAsync(id, config) 
    notifySuccess(i18n.t('info.deletedSuccessfully'))
    emit('delete')
  },
})

const updateStaffAsync = async () => {
  const updateItem = { ...editItem.value }
  if (!editItem.value.avatar_file?.payload) {
    delete updateItem.avatar_file
  }
  if (updateItem.surname_name) {
    delete updateItem.surname_name
  }

  try {
    const saved = await planningStore.upsertStaffAsync(updateItem)
    updateItem.id = updateItem.id || saved.data.id
    notifySuccess(editItem.value.id ? i18n.t('info.updatedSuccessfully') : i18n.t('info.createdSuccessfully'))
    if (!original.value?.role && editItem.value.role) {
      notifySuccess(i18n.t('info.userActivationEmailSent'))
    }
    emit('update', updateItem)
  } catch(error)  {
    console.error(error)
    notifyError(i18n.t('error.saveFailed'))
  } 
}


const onUpdate = async () => {
  isUpdating.value = true
  if ((original.value?.email !== editItem.value.email && original.value.role) || (original.value?.role && !editItem.value.role)) {
    const message = (original.value?.role && !editItem.value.role) ? 'common.confirmRoleDeleteMessage' : (editItem.value.email ? 'common.confirmEmailChangeMessage' : 'common.confirmEmailDeleteMessage')
    if (original.value?.role && !editItem.value.role) {
      message
    }
    Dialog.create({
      title: i18n.t('common.confirmChange'),
      message: i18n.t(message, { item: `${editItem.value.first_name} ${editItem.value.name}` }),
      cancel: true,
      persistent: true
    }).onOk(async () => {
      await updateStaffAsync()
      isUpdating.value = false
    })
  } else {
    await updateStaffAsync()
    isUpdating.value = false
  }
}

const onRoleChange = (value) => {
  editItem.value.role = value
  if (!editItem.value.preferred_language) {
    editItem.value.preferred_language = portalStore.selectedLanguage
  }
  if (!value) {
    editItem.value.preferred_language = null
  }
}

const showDeleteDialog = ref(false)
const onConfirmDelete = () => { showDeleteDialog.value = true }

const onDownloadAttachment = async (file) => {
  try {
    await planningStore.downloadServiceFileAsync(file.id, file.name)
  } catch (error) {
    console.error('Error downloading file:', error)
  }
}

const emit = defineEmits(['update', 'cancel', 'delete'])
useNavigator(() => emit('cancel'))
useEscapeClose(isUpdated, () => emit('cancel'))
const onCancel = () => emit('cancel')

const emailRequiredForPortal = computed(() => !!editItem.value.role && !editItem.value.email)

const step1HasError = computed(() => !editItem.value.name || !editItem.value.first_name)

const step2HasError = computed(() => (editItem.value.email && (!emailValidator(editItem.value.email) || emailAlreadyExists.value)) || (editItem.value.role && !editItem.value.email))

const step3HasError = computed(() => editItem.value.role && !editItem.value.email)

const hasErrors = computed(() => step1HasError.value || step2HasError.value || step3HasError.value)

const onSubmit = async () => {
  markAttempted(1)
  markAttempted(2)
  markAttempted(4)
  const success = await form.value.validate()
  if (hasErrors.value || !success) {
    if (step1HasError.value) step.value = 1
    else if (step2HasError.value) step.value = 2
    else if (step3HasError.value) step.value = 4
    return
  }
  await onUpdate()
}

const emailAlreadyExists = ref(false)
const validateEmailUniqueness = async (val) => {
  if (!val || !emailValidator(val)) return true
  try {
    const res = await planningStore.fetchStaffsAsync({ email: val })
    emailAlreadyExists.value = res.filter(s => s.id !== editItem.value.id).length > 0
    return !emailAlreadyExists.value || i18n.t('error.validation.emailAlreadyExists')
  } catch (e) {
    console.error(e)
    return true
  }
}

</script>
<template>
  <q-card v-drag-dialog="!isMobile" class="dialog-card-flex">
    <archive-warning-dialog
      v-model="archiveWarning"
      @confirm="async() => {
        await archiveAsync(archiveWarning.item.id, false)
        emit('update')
      }"
    />
    <confirm-dialog
      v-model="showDeleteDialog"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDelete({ id: editItem.id })"
    />
    <navigate-back
      :text="editItem?.id ? i18n.t('common.editStaff') : i18n.t('common.addStaff')"
      @cancel="onCancel"
    >
      <template v-if="!editItem?.id" #right>
        <create-mode-toggle v-model="quickCreate" />
      </template>
    </navigate-back>
    <q-card-section class="scroll col q-pa-none dialog-card-content-scroll" :class="{ 'dialog-card-content': editItem?.id }">
      <q-card-section class="q-px-sm">
        <q-form ref="form">
          <q-stepper
            v-if="quickCreate"
            v-model="quickStep"
            class="q-py-none"
          >
            <q-step
              :name="1"
              :title="i18n.t('common.quickCreate')"
              icon="mdi-flash"
            >
              <div class="row q-col-gutter-sm">
                <q-input
                  v-model="editItem.first_name"
                  :class="isMobile ? 'col-12' : 'col-4'"
                  :label="i18n.t('common.firstName')"
                  autofocus
                  required
                  :rules="[val => val && val.length > 0 || i18n.t('common.required')]"
                />
                <q-input
                  v-model="editItem.name"
                  :class="isMobile ? 'col-12' : 'col-4'"
                  :label="i18n.t('common.nameExt')"
                  required
                  :rules="[val => val && val.length > 0 || i18n.t('common.required')]"
                />
                <warehouse-selector
                  v-model="editItem.warehouse"
                  :kind="planningStore.warehouseKindEnum.PHYSICAL"
                  :class="isMobile ? 'col-12' : 'col-4'"
                  :label="i18n.t('common.workplace')"
                  required
                />
                <form-section-title :label="i18n.t('common.portalAccess')" />
                <q-input
                  v-model="editItem.email"
                  :class="isMobile ? 'col-12' : 'col-4'"
                  :label="i18n.t('common.email')"
                  type="email"
                  lazy-rules
                  :rules="[
                    val => !val || emailValidator(val) || i18n.t('common.notValid'),
                    validateEmailUniqueness
                  ]"
                >
                  <template #prepend>
                    <q-icon name="mdi-email" />
                  </template>
                </q-input>
                <q-select
                  :model-value="editItem.role"
                  :class="isMobile ? 'col-12' : 'col-4'"
                  clearable
                  :label="i18n.t('common.portalAccess')"
                  option-value="id"
                  option-label="name"
                  lazy-rules
                  :options="webSiteStore.getLocalizedRoles()"
                  :rules="[val => val == null || (editItem.email && emailValidator(editItem.email)) || i18n.t('error.validation.emailRequiredForPortalAccess')]"
                  :readonly="!webSiteStore.can(webSiteStore.permissionFlagEnum.USERS_PERMISSIONS)"
                  @update:model-value="onRoleChange"
                />
                <q-select
                  v-model="editItem.preferred_language"
                  :class="isMobile ? 'col-12' : 'col-4'"
                  :disable="!editItem.role"
                  :label="i18n.t('common.preferredLanguage')"
                  option-value="code"
                  option-label="name"
                  :readonly="!webSiteStore.can(webSiteStore.permissionFlagEnum.USERS_PERMISSIONS)"
                  :options="portalStore.getLocalizedLanguages()"
                />
              </div>
              <info-box
                v-if="editItem.role == null"
                :id="infoBoxIdEnum.PORTAL_ACCESS_ROLE_HINT"
                class="q-mt-md"
                :message="i18n.t('common.portalAccessRoleHint')"
              />
              <stepper-navigation
                :finish="() => form.validate().then(success => !hasErrors && success && onUpdate())"
                :finish-label="i18n.t('common.addStaff')"
                :loading="isUpdating"
              />
            </q-step>
          </q-stepper>
          <q-stepper
            v-else
            v-model="step"
            class="q-py-none"
          >
            <q-step
              :name="1"
              :title="i18n.t('common.basicInfo')"
              icon="mdi-folder-plus"
              :done="!!editItem.id || step > 1"
              :header-nav="!!editItem.id || step > 1"
              :error="wasAttempted(1) && step1HasError"
              error-color="negative"
              error-icon="mdi-alert-circle"
            >
              <div class="row q-col-gutter-sm">
                <q-input
                  v-model="editItem.code"
                  :class="isMobile ? 'col-4' : 'col-2'"
                  :label="i18n.t('common.code')"
                  maxlength="20"
                />
                <q-input
                  v-model="editItem.first_name"
                  :class="isMobile ? 'col-8' : 'col-3'"
                  :label="i18n.t('common.firstName')"
                  autofocus
                  required
                  :rules="[val => val && val.length > 0 || i18n.t('common.required')]"
                />
                <q-input
                  v-model="editItem.name"
                  :class="isMobile ? 'col-12' : 'col-4'"
                  :label="i18n.t('common.nameExt')"
                  required
                  :rules="[val => val && val.length > 0 || i18n.t('common.required')]"
                />
                <q-select
                  v-model="editItem.availability_type"
                  :class="isMobile ? 'col-12' : 'col-3'"
                  :label="i18n.t('common.availabilityType')"
                  option-value="id"
                  option-label="name"
                  :options="localizedServiceAvailabilityTypes"
                />
                <form-section-title :label="i18n.t('common.details')" />
                <q-input
                  v-model="editItem.description"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.description')"
                  autogrow
                />
                <q-select
                  :model-value="editItem.tags"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.tag', 2)"
                  :options="planningStore.getLocalizedServiceTags()"
                  option-value="id"
                  option-label="name"
                  use-input
                  use-chips
                  multiple
                  new-value-mode="add-unique"
                  @new-value="addPrimaryServiceTag"
                  @update:model-value="(value) => { editItem.tags = value }"
                >
                  <template #selected-item="chip">
                    <q-chip
                      removable
                      size="sm"
                      :tabindex="chip.tabindex"
                      class="q-ma-none"
                      :style="{ 'background-color': stringToColor(chip.opt.name), 'color': textColor(stringToColor(chip.opt.name)), 'margin': 0, 'margin-right': '.25rem' }"
                      @remove="chip.removeAtIndex(chip.index)"
                    >
                      {{ chip.opt.name }}
                    </q-chip>
                  </template>
                </q-select>
              </div>
              <stepper-navigation
                :continue="() => { markAttempted(1); form.validate().then((success) => !step1HasError && success && nextStep()) }"
              />
            </q-step>
            <q-step
              :name="2"
              :title="i18n.t('common.pictureContactsWorkplace')"
              icon="mdi-calendar-clock"
              :done="!!editItem.id || step > 2"
              :header-nav="!!editItem.id || step > 2"
              :error="wasAttempted(2) && step2HasError"
              error-color="negative"
              error-icon="mdi-alert-circle"
            >
              <div class="row q-col-gutter-sm">
                <div :class="isMobile ? 'col-12' : 'col-4'" class="flex flex-center">
                  <CameraGalleryInput ref="cameraGalleryInput" :edit-item="editItem" />
                  <q-btn
                    v-if="!editItem.avatar_file && !editItem.avatar_url"
                    icon="mdi-camera"
                    size="2rem"
                    round
                    @click.stop="cameraGalleryInput.onActivateCamera()"
                  />
                  <q-avatar
                    v-else
                    size="10rem"
                    style="cursor: pointer;"
                    @click.stop="cameraGalleryInput.onActivateCamera()"
                  >
                    <q-img
                      loading="lazy"
                      fit="contain"
                      :src="editItem.avatar_file?.payload || editItem.avatar_file || editItem.avatar_url"
                    >
                      <div class="absolute-full text-subtitle2 flex flex-center image-camera">
                        <q-icon name="mdi-camera" size="2rem" />
                      </div>
                    </q-img>
                  </q-avatar>
                </div>
                <div :class="isMobile ? 'col-12' : 'col-8'">
                  <div class="row q-col-gutter-sm">
                    <q-input
                      v-model="editItem.phone"
                      :class="isMobile ? 'col-12' : 'col-6'"
                      :label="i18n.t('common.phone')"
                      type="tel"
                    />
                    <q-input
                      v-model="editItem.email"
                      :class="isMobile ? 'col-12' : 'col-6'"
                      :label="i18n.t('common.email')"
                      type="email"
                      lazy-rules
                      :error="emailRequiredForPortal"
                      :error-message="emailRequiredForPortal ? i18n.t('error.validation.emailRequiredForPortalAccess') : undefined"
                      :rules="[
                        val => !editItem.role || !!val || i18n.t('error.validation.emailRequiredForPortalAccess'),
                        val => !val || emailValidator(val) || i18n.t('common.notValid'),
                        validateEmailUniqueness
                      ]"
                    >
                      <template #prepend>
                        <q-icon name="mdi-email" />
                      </template>
                    </q-input>
                    <warehouse-selector
                      v-model="editItem.warehouse"
                      :kind="planningStore.warehouseKindEnum.PHYSICAL"
                      class="col-12"
                      :label="i18n.t('common.workplace')"
                    />
                  </div>
                </div>
              </div>
              <stepper-navigation
                :continue="() => { markAttempted(2); form.validate().then((success) => !step2HasError && success && nextStep()) }"
                :back="prevStep"
              />
            </q-step>
            <q-step
              :name="3"
              :title="i18n.t('common.rateCardInfo')"
              icon="mdi-currency-usd"
              :done="!!editItem.id || step > 3"
              :header-nav="!!editItem.id || step > 3"
            >
              <rate-card-info
                :model-value="editItem"
                :mobile="isMobile"
                @update:model-value="editItem = $event"
              />
              <info-box :id="infoBoxIdEnum.RATE_CARD_GENERIC_LISTING" class="q-mt-md">
                {{ i18n.t('common.rateCardGenericListingHint') }}
                <router-link :to="{ name: 'listings' }" class="text-primary text-weight-medium" style="text-decoration: none;">
                  {{ i18n.t('common.listing', 2) }}
                  <q-icon name="mdi-open-in-new" size="xs" />
                </router-link>
              </info-box>
              <stepper-navigation
                :continue="() => { nextStep() }"
                :back="prevStep"
              />
            </q-step>
            <q-step
              :name="4"
              :title="i18n.t('common.portalAccess')"
              icon="mdi-lock-outline"
              :done="!!editItem.id || step > 4"
              :header-nav="!!editItem.id || step > 4"
              :error="wasAttempted(4) && step3HasError"
              error-color="negative"
              error-icon="mdi-alert-circle"
            >
              <div class="row q-col-gutter-sm">
                <div
                  v-if="showResendableStatus"
                  class="col-12"
                >
                  <portal-access-status-chip
                    :status="editItem.portal_access_status"
                    resendable
                    :loading="isResending"
                    @resend="onResendInvite"
                  />
                </div>
                <q-select
                  :model-value="editItem.role"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  clearable
                  :label="i18n.t('common.role')"
                  option-value="id"
                  option-label="name"
                  lazy-rules
                  :options="webSiteStore.getLocalizedRoles()"
                  :rules="[val => val == null || (editItem.email && emailValidator(editItem.email)) || i18n.t('error.validation.emailRequiredForPortalAccess')]"
                  :readonly="!webSiteStore.can(webSiteStore.permissionFlagEnum.USERS_PERMISSIONS)"
                  @update:model-value="onRoleChange"
                />
                <q-select
                  v-model="editItem.preferred_language"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :disable="!editItem.role"
                  :label="i18n.t('common.preferredLanguage')"
                  option-value="code"
                  option-label="name"
                  :readonly="!webSiteStore.can(webSiteStore.permissionFlagEnum.USERS_PERMISSIONS)"
                  :options="portalStore.getLocalizedLanguages()"
                />
              </div>
              <info-box
                v-if="editItem.role == null"
                :id="infoBoxIdEnum.PORTAL_ACCESS_ROLE_HINT"
                class="q-mt-md"
                :message="i18n.t('common.portalAccessRoleHint')"
              />
              <info-box :id="infoBoxIdEnum.ROLE_PERMISSIONS_HINT" class="q-mt-md">
                {{ i18n.t('common.rolePermissionsHint') }}
                <router-link :to="{ name: 'company', query: { tab: accountingStore.companyDataTabEnum.PERMISSIONS } }" class="text-primary text-weight-medium" style="text-decoration: none;">
                  {{ i18n.t('common.rolesAndPermissions') }}
                  <q-icon name="mdi-open-in-new" size="xs" />
                </router-link>
              </info-box>
              <stepper-navigation
                :continue="() => { nextStep() }"
                :back="prevStep"
              />
            </q-step>
            <q-step
              :name="5"
              :title="i18n.t('common.additionalInfo')"
              icon="mdi-information-outline"
              :done="!!editItem.id || step > 5"
              :header-nav="!!editItem.id || step > 5"
            >
              <div class="row q-col-gutter-sm q-mb-md">
                <q-input
                  v-model="editItem.vat_number"
                  :class="isMobile ? 'col-12' : 'col-4'"
                  :label="i18n.t('common.vatNumber')"
                  maxlength="30"
                />
                <q-input
                  v-model="editItem.iban"
                  :class="isMobile ? 'col-12' : 'col-4'"
                  :label="i18n.t('common.iban')"
                  maxlength="34"
                />
                <q-select
                  v-model="editItem.driving_licenses"
                  :class="isMobile ? 'col-12' : 'col-4'"
                  :label="i18n.t('common.drivingLicense')"
                  :options="portalStore.getLocalizedDrivingLicenseTypes()"
                  option-value="id"
                  option-label="name"
                  emit-value
                  map-options
                  multiple
                  use-chips
                />
                <form-section-title :label="i18n.t('common.reimbursements')" />
                <staff-personal-fields v-model="editItem" />
              </div>
              <file-url-edit
                bordered
                caption
                :model-value="editItem.service_files"
                :mobile="isMobile"
                @update:model-value="editItem.service_files = $event"
                @download="onDownloadAttachment"
              />
              <stepper-navigation
                :back="prevStep"
                :finish="editItem.id ? null : onSubmit"
                :finish-label="i18n.t('common.addStaff')"
                :loading="isUpdating"
              />
            </q-step>
          </q-stepper>
        </q-form>
      </q-card-section>
    </q-card-section>
    <q-card-actions v-if="editItem?.id" class="align-center dialog-card-bottom-bar">
      <q-btn
        class="large-btn q-mr-sm"
        color="negative"
        :label="i18n.t('common.delete')"
        @click="onConfirmDelete"
      />
      <q-btn
        v-if="isUpdated"
        class="large-btn"
        color="positive"
        :disable="editItem.name === ''"
        :label="i18n.t('common.update')"
        :loading="isUpdating"
        @click="onSubmit"
      />
    </q-card-actions>
  </q-card>
</template>
<style scoped>
</style>
